import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'
import { notificationsApi } from '@/services/notifications'
import type {
  ApiList,
  AppNotification,
  NotificationListQuery,
  NotificationSettingsRequest,
} from '@/types'

const KEY = 'notifications'
const UNREAD_REFETCH_INTERVAL_MS = 30_000

type NotificationListCache = ApiList<AppNotification>
type InfiniteNotificationListCache = InfiniteData<NotificationListCache>

function isUnread(notification: AppNotification) {
  return !notification.is_read && !notification.read_at
}

function updateMetadataAfterDelete(
  metadata: NotificationListCache['metadata'],
  removed: boolean,
  removedUnread: boolean
) {
  if (!removed) return metadata

  const nextUnread =
    typeof metadata.unread_count === 'number'
      ? Math.max(0, metadata.unread_count - (removedUnread ? 1 : 0))
      : metadata.unread_count

  return {
    ...metadata,
    total: Math.max(0, metadata.total - 1),
    to:
      metadata.to === null
        ? null
        : Math.max(metadata.from ?? 0, metadata.to - 1),
    unread_count: nextUnread,
  }
}

function removeNotificationFromList(
  old: NotificationListCache | undefined,
  id: string,
  removedUnread: boolean
) {
  if (!old?.data) return old

  let removed = false
  const data = old.data.filter(notification => {
    if (notification.id !== id) return true
    removed = true
    return false
  })

  if (!removed) return old

  return {
    ...old,
    data,
    metadata: updateMetadataAfterDelete(old.metadata, removed, removedUnread),
  }
}

function removeNotificationFromInfiniteList(
  old: InfiniteNotificationListCache | undefined,
  id: string,
  removedUnread: boolean
) {
  if (!old?.pages) return old

  let removed = false
  const pages = old.pages.map(page => {
    const data = page.data.filter(notification => {
      if (notification.id !== id) return true
      removed = true
      return false
    })

    return { ...page, data }
  })

  if (!removed) return old

  return {
    ...old,
    pages: pages.map(page => ({
      ...page,
      metadata: updateMetadataAfterDelete(page.metadata, true, removedUnread),
    })),
  }
}

function findNotificationInLists(
  lists: Array<[unknown, NotificationListCache | undefined]>,
  infiniteLists: Array<[unknown, InfiniteNotificationListCache | undefined]>,
  id: string
) {
  for (const [, list] of lists) {
    const match = list?.data.find(notification => notification.id === id)
    if (match) return match
  }

  for (const [, list] of infiniteLists) {
    const match = list?.pages
      .flatMap(page => page.data)
      .find(notification => notification.id === id)
    if (match) return match
  }

  return null
}

export function invalidateNotificationQueries(
  qc: Pick<QueryClient, 'invalidateQueries'>
) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: [KEY, 'unread-count'] }),
    qc.invalidateQueries({ queryKey: [KEY, 'list'] }),
    qc.invalidateQueries({ queryKey: [KEY, 'list-infinite'] }),
  ])
}

export function useNotificationsList(q: NotificationListQuery = {}) {
  return useQuery({
    queryKey: [KEY, 'list', q],
    queryFn: () => notificationsApi.list(q),
  })
}

export function useInfiniteNotificationsList(
  q: NotificationListQuery = {},
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [KEY, 'list-infinite', q],
    queryFn: ({ pageParam }) =>
      notificationsApi.list({ ...q, page: pageParam }),
    initialPageParam: q.page ?? 1,
    getNextPageParam: last => {
      const { current_page, last_page } = last.metadata
      return current_page < last_page ? current_page + 1 : undefined
    },
    enabled,
  })
}

export function useUnreadCount() {
  return useQuery({
    queryKey: [KEY, 'unread-count'],
    queryFn: () => notificationsApi.list({ limit: 1 }),
    refetchInterval: UNREAD_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    select: data => data.metadata?.unread_count ?? 0,
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useDeleteNotification() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => notificationsApi.delete(id),
    onMutate: async id => {
      await Promise.all([
        qc.cancelQueries({ queryKey: [KEY, 'unread-count'] }),
        qc.cancelQueries({ queryKey: [KEY, 'list'] }),
        qc.cancelQueries({ queryKey: [KEY, 'list-infinite'] }),
      ])

      const previousUnreadCount = qc.getQueryData<NotificationListCache>([
        KEY,
        'unread-count',
      ])
      const previousLists = qc.getQueriesData<NotificationListCache>({
        queryKey: [KEY, 'list'],
      })
      const previousInfiniteLists =
        qc.getQueriesData<InfiniteNotificationListCache>({
          queryKey: [KEY, 'list-infinite'],
        })
      const deleted = findNotificationInLists(
        previousLists,
        previousInfiniteLists,
        id
      )
      const removedUnread = deleted ? isUnread(deleted) : false

      qc.setQueryData<NotificationListCache>([KEY, 'unread-count'], old =>
        old
          ? {
              ...old,
              metadata: updateMetadataAfterDelete(
                old.metadata,
                Boolean(deleted),
                removedUnread
              ),
            }
          : old
      )
      qc.setQueriesData<NotificationListCache>(
        { queryKey: [KEY, 'list'] },
        old => removeNotificationFromList(old, id, removedUnread)
      )
      qc.setQueriesData<InfiniteNotificationListCache>(
        { queryKey: [KEY, 'list-infinite'] },
        old => removeNotificationFromInfiniteList(old, id, removedUnread)
      )

      return { previousUnreadCount, previousLists, previousInfiniteLists }
    },
    onError: (_error, _id, context) => {
      if (!context) return

      qc.setQueryData([KEY, 'unread-count'], context.previousUnreadCount)
      context.previousLists.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data)
      })
      context.previousInfiniteLists.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data)
      })
    },
    onSettled: () => invalidateNotificationQueries(qc),
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  })
}

export function useNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: [KEY, 'settings'],
    queryFn: () => notificationsApi.getSettings(),
    enabled,
  })
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NotificationSettingsRequest) =>
      notificationsApi.updateSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'settings'] }),
  })
}

export function useCompanyNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: [KEY, 'company-settings'],
    queryFn: () => notificationsApi.getCompanySettings(),
    enabled,
  })
}

export function useUpdateCompanyNotificationSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NotificationSettingsRequest) =>
      notificationsApi.updateCompanySettings(body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [KEY, 'company-settings'] }),
  })
}
